import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Globe, NavArrowDown } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import i18n, { LANGUAGE_LABEL_KEYS, LANGUAGE_STORAGE_KEY, SUPPORTED_LANGUAGES, isSupportedLanguage } from '../../i18n';
import type { SupportedLanguage } from '../../i18n';

type AuthPageShellProps = {
  children: React.ReactNode;
};

/**
 * Auth layout: logo + language control (globe, code, chevron) matching app shell pattern.
 */
export function AuthPageShell({ children }: AuthPageShellProps) {
  const { t, i18n: i18nInstance } = useTranslation('common');
  const current = (isSupportedLanguage(i18nInstance.language) ? i18nInstance.language : 'en') as SupportedLanguage;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  const handleSelect = useCallback(
    async (lang: SupportedLanguage) => {
      if (lang === current) {
        setOpen(false);
        return;
      }
      await i18n.changeLanguage(lang);
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      setOpen(false);
    },
    [current]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent | TouchEvent) => {
      const el = rootRef.current;
      if (el && !el.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
    };
  }, [open, close]);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 md:px-8">
        <img
          src="/img/innotaxes-logo.png"
          alt="innotaxes"
          className="h-8 w-auto max-w-[200px] object-contain object-left"
          decoding="async"
        />
        <div ref={rootRef} className="relative">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-1 rounded-lg border-0 bg-transparent px-1.5 py-2 text-[#131315] shadow-none transition-colors outline-none hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:ring-offset-0"
            aria-label={t('header.select_language')}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <Globe className="h-5 w-5 shrink-0" />
            <span className="text-sm font-semibold leading-none tracking-tight">{current.toUpperCase()}</span>
            <NavArrowDown className="h-4 w-4 shrink-0 text-[#131315] opacity-90" />
          </button>
          {open ? (
            <div
              role="listbox"
              aria-label={t('header.select_language')}
              className="absolute right-0 top-full z-50 mt-1 min-w-[8.5rem] rounded-xl border border-gray-200 bg-white py-1 shadow-md"
            >
              {(SUPPORTED_LANGUAGES as readonly SupportedLanguage[]).map((lang) => (
                <button
                  key={lang}
                  type="button"
                  role="option"
                  aria-selected={lang === current}
                  onClick={() => void handleSelect(lang)}
                  className={[
                    'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50',
                    lang === current ? 'font-semibold text-[#131315]' : 'font-normal text-[#525353]',
                  ].join(' ')}
                >
                  <span>{t(LANGUAGE_LABEL_KEYS[lang])}</span>
                  {lang === current ? <Check className="h-4 w-4 shrink-0" /> : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </header>
      <main className="flex w-full min-h-0 flex-1 flex-col items-center justify-center bg-white p-4 pb-8">
        {children}
      </main>
    </div>
  );
}
